import { Grid } from '@mui/material';
import { businessData as businessDataAtom } from 'atoms';
import FormHeading from 'common/FormHeading';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import StatusBadge from 'common/StatusBadge';
import StickyActionFooter from 'common/StickyActionFooter';
import { formatDate, FormatTime } from 'common/formatters';
import { useRecoilState, useRecoilValue } from 'recoil';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import Form from '../InvoicePopup/Partials/Form';
import { Elements, useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import { getStripePromise, connectedAccountIdForBusiness } from 'utils/stripePromise';
import ManualPaymentForm from '../InvoicePopup/Partials/ManualPaymentForm';
import { generateUUID } from 'utils/methods';
import { calculateFees } from 'utils/calculateFees';
import Stripe from 'stripe';
import ReactDOMServer from 'react-dom/server';
import PaymentComplete from 'emails/PaymentComplete';
import { sendEmail } from 'utils/methods';
import { useAuth } from 'context/AuthContext';


// Helper function to remove commas from price strings before parsing
const cleanPrice = (price) => {
    if (typeof price === 'string') {
        return price.replace(/,/g, '');
    }
    return price;
};

// Manual Payment Modal Component
const ManualPaymentModal = ({
    open,
    onClose,
    invoiceDetail,
    formData,
    isMakingPayment,
    setIsMakingPayment,
    manualPaymentAuthorized,
    setManualPaymentAuthorized,
    onPaymentConfirmed,
    completePaymentAndNotify,
    cleanPrice
}) => {
    
    const manualPaymentFormRef = useRef(null);
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState('');

    // Standard providers use direct charges, so Stripe.js must be bound to the
    // connected account for client-side confirmation of a new card.
    const business = useRecoilValue(businessDataAtom);
    const connectedAccountId = connectedAccountIdForBusiness(business);
    const stripePromise = useMemo(
        () => getStripePromise(connectedAccountId),
        [connectedAccountId],
    );

    const handlePay = async () => {
        if (manualPaymentFormRef.current?.handlePay) {
            await manualPaymentFormRef.current.handlePay();
        }
    };

    const payLabel = `Pay $${Number(formData.servicePrice.reduce((acc, price) => parseFloat(acc) + parseFloat(cleanPrice(price)), 0)).toFixed(2)}`;

    // Update card completion state when form ref changes
    useEffect(() => {
        const interval = setInterval(() => {
            if (manualPaymentFormRef.current) {
                const isComplete = manualPaymentFormRef.current.isCardComplete;
                const error = manualPaymentFormRef.current.cardError;
                setCardComplete(isComplete);
                if (error) setCardError(error);
            }
        }, 100);
        return () => clearInterval(interval);
    }, []);

    return (
        <FormModal 
            open={open} 
            onClose={onClose} 
            title={'Enter Payment Details'}
            subText={`To pay manually for ${formData.patientName}'s pending consultation.`}
            stickyFooter={
                <StickyActionFooter
                    onCancel={onClose}
                    onSave={handlePay}
                    cancelText="Cancel"
                    saveText={payLabel}
                    isLoading={isMakingPayment}
                    disabled={!cardComplete || !manualPaymentAuthorized || isMakingPayment}
                />
            }
        >
            <Elements stripe={stripePromise}>
                <ManualPaymentForm
                    ref={manualPaymentFormRef}
                    invoiceId={invoiceDetail?.id}
                    isMakingPayment={isMakingPayment}
                    setIsMakingPayment={setIsMakingPayment}
                    onPaymentConfirmed={onPaymentConfirmed}
                    isAuthorized={manualPaymentAuthorized}
                    patientName={formData.patientName}
                    onAuthorizationChange={setManualPaymentAuthorized}
                    patientId={formData.patient_id}
                />
            </Elements>
        </FormModal>
    );
};

const AppointmentPaymentPopup = ({ open, onClose, appointmentData }) => {
    const { token, user } = useAuth();
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    const connectedAccountId = connectedAccountIdForBusiness(business);
    const [formData, setFormData] = useState(null);
    const [providers, setProviders] = useState([]);
    const [servicesList, setServicesList] = useState([]);
    const [servicesCount, setServicesCount] = useState(1);
    const [appointmentStatus, setAppointmentStatus] = useState('');
    const [linkedInvoice, setLinkedInvoice] = useState(null);
    const [isManualPaymentOpen, setIsManualPaymentOpen] = useState(false);
    const [isMakingPayment, setIsMakingPayment] = useState(false);
    const [manualPaymentAuthorized, setManualPaymentAuthorized] = useState(false);
    const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);

    useEffect(() => {
        if (!appointmentData) return;

        const processAppointment = async () => {
            // Map appointment data to form data structure
            const patientName = appointmentData?.patients?.name || 
                `${appointmentData?.patients?.first_name || ''} ${appointmentData?.patients?.last_name || ''}`.trim() || 
                appointmentData?.patient_name || '';

            // Map service_data to serviceName and servicePrice arrays
            const serviceData = appointmentData?.service_data || [];
            const { serviceName, servicePrice } = serviceData.reduce(
                (acc, curr) => {
                    const name = curr?.name || curr?.serviceName || curr?.service_name || curr?.title || '';
                    const price = curr?.price || curr?.servicePrice || 0;
                    acc.serviceName.push(name);
                    acc.servicePrice.push(price?.toString() || '0');
                    return acc;
                },
                { serviceName: [], servicePrice: [] },
            );

            // Ensure at least one service entry if service_data is empty
            if (serviceName.length === 0) {
                serviceName.push('');
                servicePrice.push('0');
            }

            // Set services count based on service_data length
            const count = serviceName.length > 0 ? serviceName.length : 1;
            setServicesCount(count);

            // Format time for display (convert from HH:mm:ss to "2:30 PM" format)
            const rawTime = appointmentData?.time || appointmentData?.appointment_time || '';
            const formattedTime = rawTime ? FormatTime(rawTime) : '';

            const patientEmail = appointmentData?.patients?.email || '';
            // Determine provider value - if no provider_id, use business.id (for business appointments)
            const providerValue = appointmentData?.provider_id || appointmentData?.provider?.id || business?.id || null;
            
            const mappedFormData = {
                patientName: patientName,
                patient_id: appointmentData?.patient_id || appointmentData?.patients?.id || null,
                email: patientEmail,
                patientEmail: patientEmail,
                sendInvoiceByInfo: patientEmail, // Map email to sendInvoiceByInfo for display
                phone: appointmentData?.patients?.phone || '',
                dateOfService: appointmentData?.appointment_date || appointmentData?.scheduled || '',
                timeOfService: formattedTime,
                provider: providerValue,
                serviceName: serviceName,
                servicePrice: servicePrice,
                service_data: serviceData,
                total: appointmentData?.total || 0,
                payment_status: appointmentData?.payment_status || appointmentData?.status || 'pending',
            };

            setFormData(mappedFormData);
            
            // Determine status from linked invoice if available, otherwise from appointment (matching table logic)
            let finalStatus = 'pending';
            let invoice = null;
            // Try to find linked invoice
            try {
                let linkedInvoiceData = null;
                
                if (!token) {
                    console.error('No authentication token found');
                } else {
                    const response = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices?appointment_id=${appointmentData.id}&isDeleted=eq.false&limit=1&maybe_single=true`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            }
                        }
                    );

                    const result = await response.json();

                    if (!response.ok || result.error) {
                        console.error('Error fetching linked invoice:', result.error || 'Failed to fetch invoice');
                    } else {
                        // Backend returns { success: true, data: [] } or array directly
                        const data = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : null);
                        
                        // Handle maybe_single behavior - if empty array or null, return null; otherwise return first item
                        if (Array.isArray(data)) {
                            linkedInvoiceData = data.length > 0 ? data[0] : null;
                        } else {
                            linkedInvoiceData = data;
                        }
                    }
                }
                
                if (linkedInvoiceData) {
                    invoice = linkedInvoiceData;
                    setLinkedInvoice(linkedInvoiceData);
                    // Use invoice status (paid or pending)
                    finalStatus = linkedInvoiceData.payment_status || linkedInvoiceData.status || 'pending';
                    if (finalStatus?.toLowerCase() === 'paid' || linkedInvoiceData.paid_date) {
                        finalStatus = 'paid';
                    } else {
                        finalStatus = 'pending';
                    }
                } else {
                    setLinkedInvoice(null);
                    // Fallback to appointment status
                    finalStatus = appointmentData.payment_status || appointmentData.status || 'pending';
                    if (finalStatus === 'paid' || appointmentData.paid_date) {
                        finalStatus = 'paid';
                    } else if (!finalStatus || finalStatus === 'pending' || finalStatus === 'booked') {
                        finalStatus = 'pending';
                    }
                }
            } catch (error) {
                console.error('Error fetching linked invoice:', error);
                setLinkedInvoice(null);
                // Fallback to appointment status on error
                finalStatus = appointmentData.payment_status || appointmentData.status || 'pending';
                if (finalStatus === 'paid' || appointmentData.paid_date) {
                    finalStatus = 'paid';
                } else {
                    finalStatus = 'pending';
                }
            }
            
            setAppointmentStatus(finalStatus);

            // Set services list from service_data for dropdown
            if (serviceData && Array.isArray(serviceData) && serviceData.length > 0) {
                setServicesList(serviceData.map((s) => ({
                    name: s?.name || s?.serviceName || s?.service_name || s?.title || '',
                    price: s?.price || s?.servicePrice || 0,
                })));
            }

            // Set providers if available
            if (appointmentData?.provider) {
                setProviders([{
                    id: appointmentData.provider.id,
                    first_name: appointmentData.provider.first_name,
                    last_name: appointmentData.provider.last_name,
                }]);
            }
        };

        processAppointment();
    }, [appointmentData, business?.id]);

    const getTitleText = () => {
        if (!appointmentData) return 'Consultation Payment';
        return 'Consultation Payment Details';
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return formatDate(date);
        } catch (e) {
            return dateString;
        }
    };

    const getSubText = () => {
        if (!appointmentData) return '';
        
        // Get patient's first name
        const patientFirstName = appointmentData?.patients?.first_name || 
            appointmentData?.patient_name?.split(' ')[0] || 
            'Patient';
        
        // Get the booking date (created_at)
        const bookingDate = appointmentData?.created_at ? formatDateDisplay(appointmentData.created_at) : '';
        
        if (bookingDate) {
            return `Booked by ${patientFirstName} on ${bookingDate}`;
        }
        
        return '';
    };

    const createInvoiceForAppointment = async () => {
        try {
            setIsCreatingInvoice(true);
            
            if (!token) {
                console.error('No authentication token found');
                setIsCreatingInvoice(false);
                return;
            }
            
            // Calculate total from service prices
            const totalAmount = formData.servicePrice.reduce((acc, price) => parseFloat(acc) + parseFloat(cleanPrice(price)), 0);
            
            // Create invoice linked to this appointment
            const invoicePayload = {
                appointment_id: appointmentData.id,
                provider_id: appointmentData?.provider_id || null,
                patient_name: formData.patientName,
                patient_id: formData.patient_id || null,
                date_of_service: formData.dateOfService,
                time_of_service: appointmentData?.time || null,
                info_source: 'email',
                status: 'pending',
                business_id: business?.id,
                total: totalAmount,
                uuid: generateUUID(token),
                info_source_data: formData.patientEmail,
                service_data: formData.serviceName.map((name, index) => ({
                    name: name,
                    price: parseFloat(cleanPrice(formData.servicePrice[index])),
                })),
                terms_version: 'v1.2',
                invoice_type: 'Consultation',
            };

            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(invoicePayload)
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                console.error('Error creating invoice:', result.error || 'Failed to create invoice');
                alert('Failed to create invoice. Please try again.');
                return null;
            }

            // Backend returns { success: true, data: [] } or array directly
            const invoiceData = result.success && result.data !== undefined 
                ? (Array.isArray(result.data) ? result.data : [result.data])
                : (Array.isArray(result) ? result : [result]);

            if (invoiceData && invoiceData.length > 0) {
                setLinkedInvoice(invoiceData[0]);
                return invoiceData[0];
            }
            
            return null;
        } catch (error) {
            console.error('Error in createInvoiceForAppointment:', error);
            alert('Failed to create invoice. Please try again.');
            return null;
        } finally {
            setIsCreatingInvoice(false);
        }
    };

    const handleEnterManualPayment = async () => {
        // If no linked invoice exists, create one first
        if (!linkedInvoice) {
            const newInvoice = await createInvoiceForAppointment();
            if (!newInvoice) {
                return; // Failed to create invoice
            }
        }
        
        // Open manual payment modal
        setIsManualPaymentOpen(true);
        setManualPaymentAuthorized(false);
    };

    const completePaymentAndNotify = async (paymentIntentId) => {
        try {
            
            const invoiceToUpdate = linkedInvoice;
            
            if (!invoiceToUpdate) {
                console.error('No invoice to update');
                return;
            }

            // Update invoice status
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const invoiceUpdateResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoice/${invoiceToUpdate.id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        status: 'paid',
                        payment_id: paymentIntentId,
                        user_accepted_terms: true
                    })
                }
            );

            if (!invoiceUpdateResponse.ok) {
                const errorData = await invoiceUpdateResponse.json().catch(() => ({}));
                console.error('Error updating invoice:', errorData.error || 'Failed to update invoice');
                throw new Error(errorData.error || 'Failed to update invoice');
            }

            const invoiceUpdateResult = await invoiceUpdateResponse.json();
            
            // Handle response - could be object or array
            const data = invoiceUpdateResult.data[0];

            // Update appointment payment status and status to 'booked'
            if (appointmentData?.id) {
                try {
                    const appointmentUpdateResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments/${appointmentData.id}`,
                        {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                payment_status: 'paid',
                                paid_date: new Date().toISOString(),
                                status: 'booked'
                            })
                        }
                    );

                    if (!appointmentUpdateResponse.ok) {
                        const errorData = await appointmentUpdateResponse.json().catch(() => ({}));
                        console.error('Error updating appointment:', errorData.error || 'Failed to update appointment');
                        // Don't throw error here - invoice is already updated, appointment update failure shouldn't block the flow
                    }
                } catch (error) {
                    console.error('Error updating appointment:', error);
                    // Don't throw error here - invoice is already updated, appointment update failure shouldn't block the flow
                }
            }
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            console.log("paymentIntentId 3333 ",paymentIntentId);
            // Get receipt URL from Stripe. For Standard providers the charge lives on the
            // connected account, so the receipt lookup must be scoped to that account.
            const receiptQuery = connectedAccountId ? `?stripeAccountId=${connectedAccountId}` : '';
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/payment-receipt/${paymentIntentId}${receiptQuery}`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                  },
                }
              );
            
              if (!response.ok) {
                throw new Error("Failed to fetch receipt");
              }
            
              const { receiptUrl } = await response.json();

            
            const invoiceDetails = {
                patientEmail: data?.[0]?.info_source_data,
                invoiceTotal: data?.[0]?.total,
                providerId: data?.[0]?.provider_id,
            };
            
            let details = null;
            if (invoiceDetails?.providerId) {
                try {
                    const providerResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/provider?id=eq.${invoiceDetails.providerId}`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            }
                        }
                    );

                    const providerResult = await providerResponse.json();

                    if (!providerResponse.ok || providerResult.error) {
                        console.error('Error fetching provider:', providerResult.error || 'Failed to fetch provider');
                    } else {
                        // Backend returns { success: true, data: [] } or array directly
                        const fetchedProviderData = providerResult.success && providerResult.data !== undefined 
                            ? providerResult.data 
                            : (Array.isArray(providerResult) ? providerResult : []);
                        details = fetchedProviderData;
                    }
                } catch (error) {
                    console.error('Error fetching provider:', error);
                }
            } else {
                try {
                    const businessResponse = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/get-business`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({ userid: user?.id })
                        }
                    );

                    const businessResult = await businessResponse.json();

                    if (!businessResponse.ok || businessResult.error) {
                        console.error('Error fetching business:', businessResult.error || 'Failed to fetch business');
                    } else {
                        // Backend returns { success: true, data: {} } or object directly for single=true
                        const fetchedBusinessData = businessResult.success && businessResult.data !== undefined
                            ? (Array.isArray(businessResult.data) ? businessResult.data[0] : businessResult.data)
                            : (Array.isArray(businessResult) ? businessResult[0] : businessResult);
                        details = fetchedBusinessData;
                    }
                } catch (error) {
                    console.error('Error fetching business:', error);
                }
            }

            const doctorName = data?.[0]?.provider_id
                ? `${details?.[0]?.first_name} ${details?.[0]?.last_name}`
                : details?.name;
            
            const emailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={doctorName} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} services={data?.[0]?.service_data} />)}
            `;
            
            const providerBusinessEmail = data?.[0]?.provider_id
                ? details?.[0]?.email
                : details?.email;
            
            const providerEmailContent = `
                <!DOCTYPE html>
                ${ReactDOMServer.renderToString(<PaymentComplete drName={data?.[0]?.patient_name} amount={invoiceDetails?.invoiceTotal} invoiceUrl={receiptUrl} isProviderNotification={true} services={data?.[0]?.service_data} />)}
            `;
            
            if (invoiceDetails?.patientEmail) {
                sendEmail(
                    token,
                    invoiceDetails?.patientEmail,
                    'Payment Successful',
                    ' ',
                    emailContent,
                    'Payment confirmation mail has been sent to email id',
                );
            }
            
            if (providerBusinessEmail) {
                sendEmail(
                    token,
                    providerBusinessEmail,
                    'Payment Received',
                    ' ',
                    providerEmailContent,
                    'Payment notification sent to provider/business',
                );
            }

            // Close the modal and refresh
            setIsManualPaymentOpen(false);
            onClose();
            
            // Trigger refresh of the payment list
            window.dispatchEvent(new Event('invoices:refresh'));
        } catch (error) {
            console.error('Error completing payment:', error);
            alert('Payment was processed but notification failed. Please check the payment status.');
        }
    };

    const isContentReady = Boolean(appointmentData && formData);

    const getStickyFooter = () => {
        // Show "Enter Manual Payment" button only if status is pending/unpaid
        if (appointmentStatus === 'pending' || appointmentStatus === 'unpaid') {
            return (
                <StickyActionFooter
                    onCancel={handleEnterManualPayment}
                    onSave={null}
                    cancelText="Enter Manual Payment"
                    saveText=""
                    isLoading={isCreatingInvoice}
                    disabled={isCreatingInvoice}
                />
            );
        }
        
        // For paid status, just show close button
        return (
            <StickyActionFooter
                onCancel={onClose}
                onSave={null}
                cancelText="Close"
                saveText=""
                isLoading={false}
                disabled={false}
            />
        );
    };

    return (
        <>
            <FormModal
                open={open}
                onClose={onClose}
                title={getTitleText()}
                subText={getSubText()}
                badge={appointmentStatus ? <StatusBadge status={appointmentStatus} /> : null}
                stickyFooter={getStickyFooter()}
            >
                {isContentReady ? (
                    <FormStyle>
                        <Grid container spacing={0}>
                            <Grid item xs={12}>
                                <FormHeading>Patient Details</FormHeading>
                            </Grid>

                            <Form
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={() => {}} // No-op for view mode
                                providers={providers}
                                openProviderDropdown={false}
                                setOpenProviderDropdown={() => {}}
                                servicesCount={servicesCount}
                                setServicesCount={() => {}}
                                invoice={false}
                                servicesList={servicesList}
                                isViewMode={true}
                                business={business}
                                getProviderServices={() => {}}
                                getBusinessServices={() => {}}
                                purchasedService={false}
                            />
                        </Grid>
                    </FormStyle>
                ) : (
                    <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
                        Loading appointment details...
                    </div>
                )}
            </FormModal>

            {isManualPaymentOpen && formData && (linkedInvoice || isCreatingInvoice) ? (
                <ManualPaymentModal
                    open={isManualPaymentOpen}
                    onClose={() => setIsManualPaymentOpen(false)}
                    invoiceDetail={linkedInvoice}
                    formData={formData}
                    isMakingPayment={isMakingPayment}
                    setIsMakingPayment={setIsMakingPayment}
                    manualPaymentAuthorized={manualPaymentAuthorized}
                    setManualPaymentAuthorized={setManualPaymentAuthorized}
                    onPaymentConfirmed={async (paymentIntentId) => {
                        await completePaymentAndNotify(paymentIntentId);
                    }}
                    completePaymentAndNotify={completePaymentAndNotify}
                    cleanPrice={cleanPrice}
                />
            ) : null}
        </>
    );
};

export default AppointmentPaymentPopup;

